import { useEffect, useState } from 'react';
import { Input } from '~/components/form/TextField';
import { Dialog } from '~/components/ui/Dialog';
import { Button } from '../ui/buttons/Button';

type Props = {
  isOpen: boolean;
  categoryName: string;
  existingCategories: string[];
  onClose: () => void;
  onSubmit: (oldName: string, newName: string) => void;
};

export function CategoryRenameDialog({
  isOpen,
  categoryName,
  existingCategories,
  onClose,
  onSubmit,
}: Props) {
  const [newName, setNewName] = useState(categoryName);
  const [error, setError] = useState<string | null>(null);

  // Reset the input when the dialog opens with a new category
  useEffect(() => {
    if (isOpen) {
      setNewName(categoryName);
      setError(null);
    }
  }, [isOpen, categoryName]);

  const handleRename = () => {
    const trimmedName = newName.trim();

    if (!trimmedName) {
      setError('Category name is required');
      return;
    }

    if (
      trimmedName !== categoryName &&
      existingCategories.includes(trimmedName)
    ) {
      setError('A category with this name already exists');
      return;
    }

    onSubmit(categoryName, trimmedName);
    handleClose();
  };

  const handleClose = () => {
    setNewName('');
    setError(null);
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(e.target.value);
    if (error) setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newName.trim()) {
      handleRename();
    }
  };

  return (
    <Dialog show={isOpen} title='Rename Category' onClose={handleClose}>
      <div className='p-5'>
        <div className='mb-4'>
          <Input
            label='Category Name'
            value={newName}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder='Enter category name...'
            error={error || undefined}
            required
            autoFocus
          />
        </div>

        <div className='flex justify-end gap-3'>
          <Button variant='secondary' onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleRename} disabled={!newName.trim()}>
            Rename
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
